import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { MessageProvider, useMessages } from '../components/Messaging/MessageProvider';
import messageService from '../services/messageService';
import socketService from '../services/socketService';
import { formatDistanceToNow } from 'date-fns';
import { getCurrentUserId } from '../utils/authUtils';
import { API_URL } from '../utils/apiConfig';

// shadcn/ui components
import { Card, CardContent } from '../components/UI/card';
import { Avatar, AvatarFallback } from '../components/UI/avatar';
import { Badge } from '../components/UI/badge';

// Lucide icons
import { Send } from 'lucide-react';

/**
 * MessagesPageNew - Clean CoffeeConnect style Messages page
 *
 * IMPORTANT: This component renders ONLY simple text message bubbles.
 * NO invitation cards, NO meeting banners, NO action buttons in the chat feed.
 * Just clean gray (received) and blue (sent) message bubbles.
 */

/**
 * ActiveChatView - Right column showing ONLY message bubbles
 */
const ActiveChatView = ({ thread, currentUserId }) => {
  const { messages, loading, error, sendMessage } = useMessages();
  const [otherUserTyping, setOtherUserTyping] = useState(false);
  const [messageInput, setMessageInput] = useState('');
  const messagesContainerRef = useRef(null);

  const otherUser = thread?.otherParticipant;

  // Get other user info
  const avatarUrl = otherUser?.profilePhoto?.fileId
    ? `${API_URL}/api/files/file/${otherUser.profilePhoto.fileId}`
    : otherUser?.profile?.profilePicture || null;
  const initials = otherUser?.firstName?.charAt(0) || 'U';
  const fullName = `${otherUser?.firstName || ''} ${otherUser?.lastName || ''}`.trim() || 'User';
  const title = otherUser?.professionalInfo?.title ||
                otherUser?.title ||
                (otherUser?.userType === 'professional' ? 'Professional' : 'User');

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
    }
  }, [messages]);

  // Setup socket listeners for typing indicators
  useEffect(() => {
    if (!thread) return;

    const handleTyping = ({ threadId, userId }) => {
      if (threadId === thread._id && userId !== currentUserId) {
        setOtherUserTyping(true);
      }
    };

    const handleTypingStopped = ({ threadId, userId }) => {
      if (threadId === thread._id && userId !== currentUserId) {
        setOtherUserTyping(false);
      }
    };

    socketService.onTyping(handleTyping);
    socketService.onTypingStopped(handleTypingStopped);

    if (thread._id) {
      socketService.markThreadRead(thread._id);
    }

    return () => {
      setOtherUserTyping(false);
    };
  }, [thread, currentUserId]);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!messageInput.trim() || thread?.chatLocked) return;

    try {
      await sendMessage(messageInput.trim());
      setMessageInput('');
    } catch (error) {
      console.error('[MessagesPageNew] Error sending message:', error);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage(e);
    }
  };

  if (!thread) {
    return (
      <Card className="lg:col-span-2">
        <CardContent className="p-0 flex flex-col h-[600px] items-center justify-center">
          <p className="text-gray-500 dark:text-gray-400">Select a conversation to start chatting</p>
        </CardContent>
      </Card>
    );
  }

  // FILTER: Only render regular text messages - SKIP invitation_card type completely
  const textMessages = messages.filter(msg => msg.messageType !== 'invitation_card');

  return (
    <Card className="lg:col-span-2">
      <CardContent className="p-0 flex flex-col h-[600px]">
        {/* Header - Avatar, Name, Title ONLY */}
        <div className="p-4 border-b">
          <div className="flex items-center gap-3">
            <Avatar>
              {avatarUrl ? (
                <img src={avatarUrl} alt={fullName} className="object-cover" />
              ) : (
                <AvatarFallback className="bg-blue-600 text-white">
                  {initials}
                </AvatarFallback>
              )}
            </Avatar>
            <div>
              <p className="text-gray-900 dark:text-white font-medium">{fullName}</p>
              {otherUserTyping ? (
                <p className="text-sm text-blue-600 dark:text-blue-400">Typing...</p>
              ) : (
                <p className="text-sm text-gray-500 dark:text-gray-400">{title}</p>
              )}
            </div>
          </div>
        </div>

        {/* Messages - ONLY SIMPLE BUBBLES */}
        <div ref={messagesContainerRef} className="flex-1 overflow-y-auto p-4 space-y-4">
          {loading ? (
            <div className="flex justify-center items-center h-full">
              <p className="text-gray-500 dark:text-gray-400">Loading messages...</p>
            </div>
          ) : error ? (
            <div className="flex justify-center items-center h-full">
              <p className="text-red-500 dark:text-red-400">{typeof error === 'string' ? error : 'Error loading messages'}</p>
            </div>
          ) : textMessages.length === 0 ? (
            <div className="flex justify-center items-center h-full">
              <p className="text-gray-500 dark:text-gray-400">No messages yet. Start the conversation!</p>
            </div>
          ) : (
            textMessages.map((message) => {
              const isCurrentUser =
                message.sender === currentUserId ||
                message.sender?._id === currentUserId ||
                (typeof message.sender === 'object' && message.sender._id === currentUserId);

              const messageContent = message.content || '';
              const safeContent = typeof messageContent === 'string'
                ? messageContent.trim()
                : String(messageContent || '').trim();

              // Skip if no content
              if (!safeContent) return null;

              return (
                <div
                  key={message._id || message.tempId}
                  className={`flex ${isCurrentUser ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[70%] rounded-lg px-4 py-2 ${
                      isCurrentUser
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white'
                    }`}
                  >
                    <p className="text-sm whitespace-pre-wrap break-words">{safeContent}</p>
                    <p
                      className={`text-xs mt-1 ${
                        isCurrentUser ? 'text-blue-100' : 'text-gray-500 dark:text-gray-400'
                      }`}
                    >
                      {formatDistanceToNow(new Date(message.createdAt), { addSuffix: true })}
                    </p>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Input */}
        <form onSubmit={handleSendMessage} className="p-4 border-t">
          <div className="flex gap-2">
            <input
              type="text"
              placeholder={thread?.chatLocked ? "This chat is locked" : "Type a message..."}
              value={messageInput}
              onChange={(e) => setMessageInput(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={thread?.chatLocked}
              className="flex-1 px-4 py-2 bg-gray-100 dark:bg-gray-700 rounded-full text-sm text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 border-none focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
            />
            <button
              type="submit"
              disabled={!messageInput.trim() || thread?.chatLocked}
              className="h-10 w-10 bg-black hover:bg-gray-800 text-white rounded-md flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <Send className="h-4 w-4" />
            </button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
};

/**
 * ConversationList - Left column with conversation list
 */
const ConversationList = ({ threads, selectedThreadId, onThreadSelect, currentUserId }) => {
  // Define avatar colors
  const avatarColors = ['bg-blue-600', 'bg-purple-600', 'bg-green-600', 'bg-pink-600', 'bg-indigo-600'];

  return (
    <Card className="lg:col-span-1">
      <CardContent className="p-4">
        {threads.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-gray-500 dark:text-gray-400">No conversations yet</p>
          </div>
        ) : (
          <div className="space-y-2">
            {threads.map((thread) => {
              const otherUser = thread.otherParticipant;
              if (!otherUser) return null;

              const avatarUrl = otherUser?.profilePhoto?.fileId
                ? `${API_URL}/api/files/file/${otherUser.profilePhoto.fileId}`
                : otherUser?.profile?.profilePicture || null;

              const initials = `${otherUser.firstName?.charAt(0) || ''}${otherUser.lastName?.charAt(0) || ''}`.toUpperCase() || 'U';
              const fullName = `${otherUser.firstName || ''} ${otherUser.lastName || ''}`.trim() || 'User';
              const title = otherUser?.professionalInfo?.title || otherUser?.title || 'User';
              const lastMessage = thread.lastMessage?.content || 'Start a conversation';
              const timestamp = thread.lastMessage?.timestamp || thread.updatedAt;

              let unreadCount = 0;
              if (thread.unreadCount && typeof thread.unreadCount === 'object') {
                unreadCount = thread.unreadCount[currentUserId] || 0;
              }

              const colorIndex = (otherUser.firstName?.charCodeAt(0) || 0) % avatarColors.length;
              const avatarColor = avatarColors[colorIndex];
              const isSelected = selectedThreadId === thread._id;

              return (
                <div
                  key={thread._id}
                  onClick={() => onThreadSelect(thread._id)}
                  className={`p-4 rounded-lg cursor-pointer transition-colors ${
                    isSelected ? 'bg-blue-50 dark:bg-blue-900/20' : 'hover:bg-gray-50 dark:hover:bg-gray-700/50'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <Avatar className={!avatarUrl ? avatarColor : ''}>
                      {avatarUrl ? (
                        <img src={avatarUrl} alt={fullName} className="object-cover" />
                      ) : (
                        <AvatarFallback className={`${avatarColor} text-white`}>
                          {initials}
                        </AvatarFallback>
                      )}
                    </Avatar>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <p className="text-sm text-gray-900 dark:text-white font-medium truncate">
                          {fullName}
                        </p>
                        {unreadCount > 0 && (
                          <Badge variant="default" className="bg-black dark:bg-white text-white dark:text-black text-xs">
                            {unreadCount}
                          </Badge>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{title}</p>
                      <p className="text-sm text-gray-600 dark:text-gray-300 truncate mt-1">
                        {lastMessage}
                      </p>
                      {timestamp && (
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          {formatDistanceToNow(new Date(timestamp), { addSuffix: true })}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

/**
 * MessagesPageNew - Main component
 */
const MessagesPageNew = () => {
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedThreadId, setSelectedThreadId] = useState(null);
  const [selectedThreadFull, setSelectedThreadFull] = useState(null);

  const { currentUser } = useAuth();
  const currentUserId = getCurrentUserId();

  // Load threads on mount
  useEffect(() => {
    const loadThreads = async () => {
      if (!currentUser) {
        setThreads([]);
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const fetchedThreads = await messageService.getThreads();
        const sortedThreads = (fetchedThreads || []).sort((a, b) => {
          const aTime = new Date(a.lastMessage?.timestamp || a.updatedAt || 0);
          const bTime = new Date(b.lastMessage?.timestamp || b.updatedAt || 0);
          return bTime.getTime() - aTime.getTime();
        });

        setThreads(sortedThreads);
        setError(null);
      } catch (err) {
        console.error('[MessagesPageNew] Error loading threads:', err);
        setError('Failed to load conversations');
        setThreads([]);
      } finally {
        setLoading(false);
      }
    };

    loadThreads();
  }, [currentUser]);

  // Socket event handlers
  const handleNewMessage = useCallback((messageData) => {
    if (!messageData || !messageData.threadId) return;

    const { threadId, content, createdAt, sender } = messageData;
    const senderId = sender?._id || sender;

    setThreads(prevThreads => {
      const updatedThreads = prevThreads.map(thread => {
        if (thread._id === threadId) {
          return {
            ...thread,
            lastMessage: {
              content: content || '',
              timestamp: createdAt || new Date().toISOString(),
              sender: senderId
            },
            updatedAt: createdAt || new Date().toISOString(),
            unreadCount: senderId !== currentUserId
              ? { ...thread.unreadCount, [currentUserId]: (thread.unreadCount?.[currentUserId] || 0) + 1 }
              : thread.unreadCount
          };
        }
        return thread;
      });

      return updatedThreads.sort((a, b) => {
        const aTime = new Date(a.lastMessage?.timestamp || a.updatedAt || 0);
        const bTime = new Date(b.lastMessage?.timestamp || b.updatedAt || 0);
        return bTime.getTime() - aTime.getTime();
      });
    });
  }, [currentUserId]);

  const handleNewThread = useCallback((threadData) => {
    if (!threadData || !threadData._id) return;

    setThreads(prevThreads => {
      const exists = prevThreads.find(t => t._id === threadData._id);
      if (exists) return prevThreads;
      return [threadData, ...prevThreads];
    });
  }, []);

  const handleThreadRead = useCallback((readData) => {
    if (!readData || !readData.threadId) return;

    setThreads(prevThreads => {
      return prevThreads.map(thread => {
        if (thread._id === readData.threadId) {
          return {
            ...thread,
            unreadCount: {
              ...thread.unreadCount,
              [currentUserId]: 0
            }
          };
        }
        return thread;
      });
    });
  }, [currentUserId]);

  // Setup Socket.IO listeners
  useEffect(() => {
    if (!currentUser) return;

    if (!socketService.isSocketConnected()) {
      socketService.connect().catch(err => {
        console.error('[MessagesPageNew] Socket connection failed:', err);
      });
    }

    socketService.on('new-message', handleNewMessage);
    socketService.on('new-thread', handleNewThread);
    socketService.on('thread-read', handleThreadRead);
    socketService.on('messages-read', handleThreadRead);

    return () => {
      socketService.off('new-message', handleNewMessage);
      socketService.off('new-thread', handleNewThread);
      socketService.off('thread-read', handleThreadRead);
      socketService.off('messages-read', handleThreadRead);
    };
  }, [currentUser, handleNewMessage, handleNewThread, handleThreadRead]);

  // Join thread rooms
  useEffect(() => {
    if (!currentUser || !socketService.isSocketConnected() || !threads.length) return;

    threads.forEach(thread => {
      if (thread._id) {
        socketService.emitEvent('join-thread', thread._id);
      }
    });
  }, [currentUser, threads]);

  // Handle thread selection
  const handleThreadSelect = useCallback((threadId) => {
    const selected = threads.find(t => t._id === threadId);
    if (selected) {
      setSelectedThreadFull(selected);
      setSelectedThreadId(threadId);
    }
  }, [threads]);

  if (loading && threads.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <p className="text-gray-500 dark:text-gray-400">Loading conversations...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900" style={{ padding: '32px 16px' }}>
      <div className="mx-auto" style={{ maxWidth: '1280px' }}>
        <h1 className="text-3xl mb-8 text-gray-900 dark:text-white">Messages</h1>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Left Column - Conversation List */}
          <ConversationList
            threads={threads}
            selectedThreadId={selectedThreadId}
            onThreadSelect={handleThreadSelect}
            currentUserId={currentUserId}
          />

          {/* Right Column - Active Chat */}
          {selectedThreadId && selectedThreadFull ? (
            <MessageProvider
              key={`thread-${selectedThreadId}`}
              threadId={selectedThreadId}
              userId={currentUserId}
            >
              <ActiveChatView
                thread={selectedThreadFull}
                currentUserId={currentUserId}
              />
            </MessageProvider>
          ) : (
            <Card className="lg:col-span-2">
              <CardContent className="p-0 flex flex-col h-[600px] items-center justify-center">
                <p className="text-gray-500 dark:text-gray-400">Select a conversation to start chatting</p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default MessagesPageNew;
